/* eslint-disable eqeqeq */
import React, { useState, useContext, useMemo, useEffect } from 'react';
import { ArrowDownIcon, ArrowUpIcon, CheckCircleIcon } from '@heroicons/react/solid';
import { DropDownList } from 'components/formInputs/dropDownList';
import { Input } from 'components/formInputs/input';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { TranslatableInput } from 'components/formInputs/tranlatableInput';
import {
  fillTranslatableTexts,
  getValidationPairsForTranslatableText,
  getValidationShapeForTranslatableText,
  TranslationFieldOptions,
} from 'utils/schemaHelper';
import { useNavigate } from 'react-router-dom';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Config } from 'configs';
import { DataColumn, LinkColumn, List } from 'components/lists';
import { getTranslatedData } from 'locales';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { updateRestrictedResources } from 'api/restaurant';
import { apiActionChooser } from 'utils/apiActionChooser';
import { VariationTemplatesOptionAddEditForm } from './_popup_options';

export const TemplateVariationForm = React.forwardRef(
  (
    { itemData, isEdit, children, isProductVariation, productId, navigateAfterSubmitCallback, isSynced, allProducts, isAttachedOnEdit, setIsSaving },
    ref,
  ) => {
    const urlRootName = 'variation-templates';
    const isAddNew = !isEdit;
    const [selectedVariationOption, setSelectedVariationOption] = useState(null);
    const [options, setOptions] = useState(itemData?.items || []);
    const [modalIsVisible, setModalIsVisible] = useState(false);
    const [variationType, setVariationType] = useState(itemData?.variation_type || null);
    const [isRequired, setIsRequired] = useState(false);
    const navigate = useNavigate();
    const templateNameFieldIsVisible = !isProductVariation;

    const { addNew, editById } = useContext(CompanyContext);
    const { state: auth } = useContext(AuthContext);

    const schema = yup.object().shape({
      _id: yup.string().default(itemData?._id),
      isEditForm: yup.boolean().default(isEdit),
      isForProductVaration: yup.boolean().default(isProductVariation),
      template_name: yup
        .string()
        .when('isForProductVaration', {
          is: false,
          then: yup.string().required('Please enter a template name.'),
        })
        // .required('Please enter a template name.')
        .default(itemData?.template_name),
      order: yup
        .number()
        .min(0)
        .max(999)
        .nullable(true)
        // checking self-equality works for NaN, transforming it to null
        .transform((_, val) => (val == Number(val) ? Number(val) : null))
        .default(itemData?.order),
      name: yup
        .object()
        .shape(
          getValidationShapeForTranslatableText(
            itemData?.name,
            TranslationFieldOptions.REQUIRE_AT_LEAST_ONE,
          ),
          getValidationPairsForTranslatableText(),
        ),
      hint: yup
        .object()
        .shape(
          getValidationShapeForTranslatableText(
            itemData?.hint,
            TranslationFieldOptions.OPTIONAL,
          ),
        ),
      variation_type: yup.string().default(itemData?.variation_type),
      min_of_multi: yup
        .number()
        .min(0)
        .max(999)
        .nullable(true)
        .transform((_, val) => (val == Number(val) ? Number(val) : null))
        .when('variation_type', {
          is: 'multi_selection_min',
          then: yup
            .number()
            .min(0, 'Please enter a valid minimum value.')
            .max(999, 'Please enter a valid minimum value.')
            .nullable(false)
            .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : -1))
            .required('Please enter min value.'),
        })
        .default(itemData?.min_of_multi || null),
      max_of_multi: yup
        .number()
        .min(0)
        .max(999)
        .nullable(true)
        .transform((_, val) => (val == Number(val) ? Number(val) : null))
        .when('variation_type', {
          is: 'multi_selection_max',
          then: yup
            .number()
            .min(0, 'Please enter a valid maximum value.')
            .max(999, 'Please enter a valid maximum value.')
            .nullable(false)
            .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : -1))
            .required('Please enter max value.'),
        })
        .default(itemData?.max_of_multi || null),
      required: yup.boolean().default(itemData?.required || false),
      attachable: yup.boolean().default(itemData?.attachable || false),
      hidden: yup.boolean().default(itemData?.hidden || false),
      hidden_dinein: yup.boolean().default(itemData?.hidden_dinein || false),
      hidden_delivery: yup.boolean().default(itemData?.hidden_delivery || false),
      items: yup.array().of(
        yup.object().shape({
          id: yup.string().required('Unique identifier is required for variation.'),
          // name: yup.string().required('Variation name is required.'),
          name: yup.object()
            .when('combo_product', {
              is: (val) => !val,
              then: yup.object().shape(
                getValidationShapeForTranslatableText(
                  null,
                  TranslationFieldOptions.REQUIRE_AT_LEAST_ONE,
                ),
                getValidationPairsForTranslatableText(),
              ),
              otherwise: yup.object().shape(
                getValidationShapeForTranslatableText(
                  null,
                  TranslationFieldOptions.OPTIONAL,
                ),
                getValidationPairsForTranslatableText(),
              ),
            }),
          price: yup.number().nullable(true),
          combo_product: yup.string().nullable(true),
        }),
      ),
      manual_rkeeper_group_id: yup.string().default(itemData?.manual_rkeeper_group_id),
    });

    const {
      register,
      reset,
      handleSubmit,
      formState: { errors },
      clearErrors,
    } = useForm({
      defaultValues: useMemo(() => {
        return schema.default();
      }, [schema]),
      resolver: yupResolver(schema),
    });

    const onSubmit = async (data) => {
      if (setIsSaving) {
        setIsSaving(true);
      }

      const submitedData = data;

      submitedData.items = options;

      if (isProductVariation) {
        submitedData.product = productId;
        delete submitedData.attachable;
      }

      delete submitedData.isEditForm;
      delete submitedData.isForProductVaration;
      delete submitedData._meta;

      if (submitedData.variation_type === Config.DATA.VARIATION_TYPES[0].value) {
        submitedData.min_of_multi = null;
        submitedData.max_of_multi = null;
      }

      const submitData = { ...submitedData, company: auth.company?.company };

      submitData.hint = fillTranslatableTexts(data.hint);
      submitData.name = fillTranslatableTexts(data.name);

      if (submitData.items) {
        submitData.items.forEach((item) => {
          // eslint-disable-next-line no-param-reassign
          item.name = fillTranslatableTexts(item.name);
        });
      }

      const collectionName = isProductVariation
        ? Config.DATA.COLLECTIONS.PRODUCT_VARIATIONS
        : Config.DATA.COLLECTIONS.VARIATION_TEMPLATES;

      const navigateAfterSubmit = () => {
        if (navigateAfterSubmitCallback) {
          navigateAfterSubmitCallback();
        } else {
          navigate(`/settings/${urlRootName}`);
        }
      };

      if (isAddNew) {
        delete submitData._id;

        if (isProductVariation) {
          if (itemData && itemData.attachable) {
            submitData.template = itemData._id;
          }

          delete submitedData.template_name;
        }

        await addNew(
          {
            collectionName,
            data: submitData,
            token: auth.token,
            resourceV2: isProductVariation ? 'AdminProductVariation' : 'AdminVariationTemplate',
            companyIdForV2: auth.company?.company,
          },
          () => {
            navigateAfterSubmit();
          },
          () => {
            if (setIsSaving) {
              setIsSaving(false);
            }
          },
        );
      } else {
        await apiActionChooser(auth.company?.company, async () => {
          const updateClause = {
            $set: {
              variation_type: submitData.variation_type,
              order: submitData.order,
              name: submitData.name,
              hint: submitData.hint,
              min_of_multi: submitData.min_of_multi,
              max_of_multi: submitData.max_of_multi,
              required: submitData.required,
              hidden: submitData.hidden,
              hidden_dinein: submitData.hidden_dinein,
              hidden_delivery: submitData.hidden_delivery,
              items: submitData.items.map((x) => ({
                ...x,
                combo_product: x.combo_product ? { $oid: x.combo_product } : null,
              })),
            },
          };

          if (isProductVariation) {
            updateClause.$set.manual_rkeeper_group_id = submitData.manual_rkeeper_group_id;

            if (isAttachedOnEdit) {
              delete updateClause.$set.variation_type;
              delete updateClause.$set.name;
              delete updateClause.$set.hint;
              delete updateClause.$set.min_of_multi;
              delete updateClause.$set.max_of_multi;
              delete updateClause.$set.required;
              delete updateClause.$set.hidden;
              delete updateClause.$set.hidden_dinein;
              delete updateClause.$set.hidden_delivery;
            }
          } else {
            updateClause.$set.template_name = submitData.template_name;
            updateClause.$set.attachable = submitData.attachable;
          }

          await editById(
            {
              collectionName,
              id: submitData._id,
              updateClause,
              token: auth.token,
            },
            () => {
              if (!isProductVariation && itemData && itemData.attachable) {
                updateRestrictedResources({
                  token: auth.token,
                  updateKey: 'variation_template_attached',
                  values: { _id: submitData._id },
                  // alertController,
                }).then(() => {
                  navigateAfterSubmit();
                }).catch(() => {
                  navigateAfterSubmit();
                });
              } else {
                navigateAfterSubmit();
              }
            },
            () => {
              if (setIsSaving) {
                setIsSaving(false);
              }
            },
          );
        }, async () => {
          const updateClause = {
            variation_type: submitData.variation_type,
            order: submitData.order,
            name: submitData.name,
            hint: submitData.hint,
            min_of_multi: submitData.min_of_multi,
            max_of_multi: submitData.max_of_multi,
            required: submitData.required,
            hidden: submitData.hidden,
            hidden_dinein: submitData.hidden_dinein,
            hidden_delivery: submitData.hidden_delivery,
            items: submitData.items.map((x) => ({
              ...x,
              combo_product: x.combo_product ? x.combo_product : null,
            })),
          };

          if (isProductVariation) {
            updateClause.manual_rkeeper_group_id = submitData.manual_rkeeper_group_id;

            if (isAttachedOnEdit) {
              delete updateClause.variation_type;
              delete updateClause.name;
              delete updateClause.hint;
              delete updateClause.min_of_multi;
              delete updateClause.max_of_multi;
              delete updateClause.required;
              delete updateClause.hidden;
              delete updateClause.hidden_dinein;
              delete updateClause.hidden_delivery;
            }
          } else {
            updateClause.template_name = submitData.template_name;
            updateClause.attachable = submitData.attachable;
          }

          await editById(
            {
              id: submitData._id,
              updateClause,
              resourceV2: isProductVariation ? 'AdminProductVariation' : 'AdminVariationTemplate',
              updateTypeV2: isProductVariation && isAttachedOnEdit ? 'AttachedGeneral' : 'General',
              updateDataV2: updateClause,
              companyIdForV2: auth.company?.company,
            },
            () => {
              navigateAfterSubmit();
            },
            () => {
              if (setIsSaving) {
                setIsSaving(false);
              }
            },
          );
        });
      }
    };
    const onError = (data) => console.log(data);

    useEffect(() => {
      setOptions(itemData?.items || []);
      setIsRequired(itemData?.required || false);
      setVariationType(itemData?.variation_type || Config.DATA.VARIATION_TYPES[0].value);
      reset(itemData);
    }, [itemData, reset]);

    const deleteOption = (optionItem) => {
      const newOptions = options.filter((option) => option.id !== optionItem.id);

      setOptions(newOptions);
    };

    const editOption = (optionItem) => {
      setSelectedVariationOption(optionItem);
      setModalIsVisible(true);
    };

    const onVariationTypeChange = (e) => {
      setVariationType(e.target.value);
    };

    const reorderOptions = (ind, isUp) => {
      setOptions((prev) => {
        const newArray = [...prev];
        const temp1 = newArray[ind];

        if (isUp) {
          const temp2 = newArray[ind - 1];

          newArray[ind] = temp2;
          newArray[ind - 1] = temp1;
        } else {
          const temp2 = newArray[ind + 1];

          newArray[ind] = temp2;
          newArray[ind + 1] = temp1;
        }

        return newArray;
      });
    };

    return (
      <>
        {modalIsVisible && (
          <VariationTemplatesOptionAddEditForm
            itemData={selectedVariationOption}
            isOpen={modalIsVisible}
            closeModal={() => setModalIsVisible(false)}
            onSave={(item) => {
              const index = options.findIndex((o) => o.id === item.id);

              if (index !== -1) {
                const newOptions = [...options];

                newOptions.splice(index, 1, item);
                setOptions(newOptions);
              } else {
                setOptions((oldArray) => [...oldArray, item]);
              }

              setModalIsVisible(false);
              setSelectedVariationOption(null);
            }}
            isProductVariation={isProductVariation}
            isAttached={isAttachedOnEdit || (isProductVariation && itemData && itemData.attachable)}
            isSynced={isSynced}
            allProducts={allProducts}
          />
        )}{' '}
        <form onSubmit={handleSubmit(onSubmit, onError)} method="POST">
          <div className="grid grid-cols-6 gap-6">
            {children}
            {templateNameFieldIsVisible && (
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="template-name" className="text-left block text-sm font-medium text-gray-700">
                  Template name*
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <Input
                    {...register('template_name')}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    name="template_name"
                    id="template-name"
                    type="text"
                    errorMessage={errors?.template_name}
                  />
                </div>
                {errors?.template_name && (
                  <p className="mt-2 text-sm text-red-600" id="email-error">
                    {errors?.template_name.message}
                  </p>
                )}
              </div>
            )}

            <div className="text-left col-span-6 sm:col-span-3">
              <DropDownList
                {...register('variation_type')}
                showLabel
                id="variation_type"
                name="variation_type"
                label="Variation type"
                options={Config.DATA.VARIATION_TYPES}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                onChange={(e) => onVariationTypeChange(e)}
                disabled={isAttachedOnEdit || (isProductVariation && itemData && itemData.attachable)}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <label htmlFor="order" className="text-left block text-sm font-medium text-gray-700">
                Order
              </label>
              <div className="mt-1">
                <Input
                  {...register('order')}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  name="order"
                  id="order"
                  type="text"
                />
              </div>
            </div>
            {templateNameFieldIsVisible && <div className="col-span-6 sm:col-span-3 " />}

            <div className="col-span-6 sm:col-span-3">
              <TranslatableInput
                name="name"
                register={register}
                error={errors?.name}
                containerClassName="sm:col-span-3"
                labelClassName="block text-sm font-medium text-gray-700"
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Variation name*"
                onChange={() => {
                  if (errors?.name) {
                    clearErrors('name');
                  }
                }}
                disabled={isAttachedOnEdit || (isProductVariation && itemData && itemData.attachable)}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <TranslatableInput
                name="hint"
                register={register}
                error={errors?.hint}
                containerClassName="sm:col-span-3"
                labelClassName="block text-sm font-medium text-gray-700"
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Variation hint"
                disabled={isAttachedOnEdit || (isProductVariation && itemData && itemData.attachable)}
              />
            </div>

            {variationType === Config.DATA.VARIATION_TYPES[1].value && (
              <>
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="min_of_multi" className="block text-sm font-medium text-gray-700">
                    Min value
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <Input
                      {...register('min_of_multi')}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      name="min_of_multi"
                      id="min_of_multi"
                      type="text"
                      disabled={isAttachedOnEdit || (isProductVariation && itemData && itemData.attachable)}
                    />
                  </div>
                  {errors?.min_of_multi && (
                    <p className="mt-2 text-sm text-red-600" id="min_of_multi-error">
                      {errors?.min_of_multi.message}
                    </p>
                  )}
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="max_of_multi" className="block text-sm font-medium text-gray-700">
                    Max value
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <Input
                      {...register('max_of_multi')}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      name="max_of_multi"
                      id="max_of_multi"
                      type="text"
                      disabled={isAttachedOnEdit || (isProductVariation && itemData && itemData.attachable)}
                    />
                  </div>
                  {errors?.max_of_multi && (
                    <p className="mt-2 text-sm text-red-600" id="max_of_multi-error">
                      {errors?.max_of_multi.message}
                    </p>
                  )}
                </div>
              </>
            )}

            <CheckboxWrapper
              register={register}
              name="required"
              title="Required"
              subtitle="is this required to select in app"
              responsiveClassName="col-span-6 sm:col-span-3"
              onChange={() => setIsRequired(!isRequired)}
              disabled={isAttachedOnEdit || (isProductVariation && itemData && itemData.attachable)}
            />

            <div className="col-span-6 sm:col-span-3">
              <CheckboxWrapper
                register={register}
                name="hidden"
                title="Hidden"
                subtitle="hidden everywhere"
                responsiveClassName="col-span-6"
                disabled={isAttachedOnEdit || (isProductVariation && itemData && itemData.attachable)}
              />

              <CheckboxWrapper
                register={register}
                name="hidden_dinein"
                title="Hidden dine-in"
                subtitle="hidden only in dine-in"
                responsiveClassName="col-span-6 mt-2"
                disabled={isAttachedOnEdit || (isProductVariation && itemData && itemData.attachable)}
              />

              <CheckboxWrapper
                register={register}
                name="hidden_delivery"
                title="Hidden delivery"
                subtitle="hidden only in delivery/takeaway"
                responsiveClassName="col-span-6 mt-2"
                disabled={isAttachedOnEdit || (isProductVariation && itemData && itemData.attachable)}
              />
            </div>

            {!isProductVariation && (
            <CheckboxWrapper
              register={register}
              name="attachable"
              title="Attachable"
              subtitle="editing this template will edit product variations"
              responsiveClassName="col-span-6 sm:col-span-3"
            />
            )}

            {isProductVariation && (
            <div className="col-span-6">
              <fieldset className="mt-4">
                <div className="mb-6">
                  <legend className="text-base font-medium text-gray-900">External modifier group (optional)</legend>
                  <p className="text-sm text-gray-500 mt-1">
                    Modifier group identifier code from integration
                  </p>
                </div>
                <div className="grid grid-cols-6 gap-6">
                  {!isSynced && (
                  <TextboxWrapper
                    register={register}
                    name="manual_rkeeper_group_id"
                    title="External Group ID"
                    responsiveClassName="col-span-6 sm:col-span-3"
                    error={errors?.manual_rkeeper_group_id}
                  />
                  )}
                  {isSynced && (
                  <TextboxWrapper
                    value={itemData?.rkeeper_group_id}
                    name="rkeeper_group_id"
                    title="External Group ID"
                    responsiveClassName="col-span-6 sm:col-span-3"
                    disabled
                  />
                  )}
                </div>
              </fieldset>
            </div>
            )}

            <div className="col-span-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-300" />
                </div>
              </div>
            </div>
            <div className="col-span-6 text-left">
              <div>
                <div className="sm:flex sm:items-center">
                  <div className="sm:flex-auto">
                    <h1 className="text-xl font-semibold text-gray-900">Options</h1>
                    <p className="mt-2 text-sm text-gray-700">
                      A list of all the options of current variation.
                    </p>
                  </div>
                  {!isSynced && (!isProductVariation || (isProductVariation && (!itemData || (itemData && !itemData.attachable)) && !isAttachedOnEdit)) && (
                  <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
                      onClick={() => {
                        setSelectedVariationOption(null);
                        setModalIsVisible(true);
                      }}
                    >
                      Add option
                    </button>
                  </div>
                  )}
                </div>
                {!!options && options.length > 0 && (

                <List source={options} onRowClick={editOption}>
                  <DataColumn
                    name="Name"
                    dbColumnName="name"
                    render={(item) => {
                      const optionName = getTranslatedData(item.name);

                      if (optionName) {
                        return optionName;
                      }

                      if (item.combo_product) {
                        const foundProduct = allProducts.find((x) => x._id === item.combo_product);

                        if (foundProduct) {
                          return getTranslatedData(foundProduct.name);
                        }
                      }

                      return optionName;
                    }}
                    headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                  />
                  <DataColumn
                    name="Price"
                    dbColumnName="price"
                    headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                  />
                  <DataColumn
                    name="Hidden dine-in"
                    dbColumnName="hidden_dinein"
                    render={(item) => {
                      return item.hidden || item.hidden_dinein ? <CheckCircleIcon className="h-5 w-5" aria-hidden="true" /> : null;
                    }}
                    headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                  />
                  <DataColumn
                    name="Hidden delivery"
                    dbColumnName="hidden_delivery"
                    render={(item) => {
                      return item.hidden || item.hidden_delivery ? <CheckCircleIcon className="h-5 w-5" aria-hidden="true" /> : null;
                    }}
                    headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                  />
                  {isProductVariation && (
                  <DataColumn
                    name="Combo Product"
                    dbColumnName="combo_product"
                    render={(item) => {
                      if (allProducts) {
                        const foundProduct = allProducts.find((x) => x._id === item.combo_product);

                        if (foundProduct) {
                          return getTranslatedData(foundProduct.name);
                        }
                      }

                      return item.combo_product;
                    }}
                    headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                  />
                  )}
                  <DataColumn
                    name="Reorder"
                    dbColumnName="reorder"
                    render={(item, ind) => {
                      return (
                        <div className="flex flex-row">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              reorderOptions(ind, true);
                            }}
                            className="p-1 group disabled:opacity-60"
                            disabled={ind === 0}
                          >
                            <ArrowUpIcon
                              className="h-5 w-5 text-indigo-500 group-hover:text-indigo-400"
                            />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              reorderOptions(ind, false);
                            }}
                            className="p-1 group disabled:opacity-60"
                            disabled={ind === options.length - 1}
                          >
                            <ArrowDownIcon
                              className="h-5 w-5 text-indigo-500 group-hover:text-indigo-400"
                            />
                          </button>
                        </div>
                      );
                    }}
                    headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                  />
                  {!isSynced && (!isProductVariation || (isProductVariation && (!itemData || (itemData && !itemData.attachable)) && !isAttachedOnEdit)) && (
                    <LinkColumn
                      title="Delete"
                      onClick={deleteOption}
                      className="relative py-3.5 pl-3 sm:pr-6"
                      dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                    />
                  )}
                </List>
                )}
              </div>
            </div>
          </div>
          <input id="hidden_submit_button" type="submit" style={{ display: 'none' }} ref={ref} />
        </form>
      </>
    );
  },
);
